import React, { useCallback, useEffect, useRef, useState } from 'react';

const MAX_ANTAL = 3;

const versionsText = (v) => `Version ${Number(v.version || 1).toFixed(1)}`;
const datumText = (d) => (d ? new Date(d).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

/**
 * Jämför estimatversioner sida vid sida — samma upplägg som kundens
 * offertjämförelse i MyProjects: välj 2–3, visa i helskärm, valfri synkad
 * scrollning.
 *
 * Varje kolumn renderas av `renderVersion(id)` från EstimatView. Komponenten
 * importerar alltså inte EstimatView själv, vilket hade blivit en cirkel.
 *
 * @param {Array} versioner - [{ _id, version, sentAt, senaste? }], nyast först
 * @param {Function} renderVersion - (estimatId) => ReactNode, i läsläge
 * @param {Function} onClose
 */
const VersionsJamforelse = ({ versioner, renderVersion, onClose }) => {
  // Förval: den senaste och versionen före — det vanligaste man vill jämföra
  const [valda, setValda] = useState(() => versioner.slice(0, 2).map(v => String(v._id)));
  const [visar, setVisar] = useState(false);
  const [synkadScroll, setSynkadScroll] = useState(false);
  const scrollRefs = useRef([]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const vaxla = (id) => {
    setValda(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= MAX_ANTAL) return prev;
      return [...prev, id];
    });
  };

  const handleScroll = useCallback((index, scrollTop) => {
    if (!synkadScroll) return;
    scrollRefs.current.forEach((ref, i) => {
      if (ref && i !== index) ref.scrollTop = scrollTop;
    });
  }, [synkadScroll]);

  // Kolumnerna i samma ordning som listan (nyast först), inte i klickordning
  const valdaVersioner = versioner.filter(v => valda.includes(String(v._id)));

  if (!visar) {
    return (
      <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true">
        <div className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-lg shadow-xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Välj versioner att jämföra</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" aria-label="Stäng">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="px-5 py-4">
            <p className="text-sm text-gray-600 mb-3">Välj 2–3 versioner som du vill se sida vid sida:</p>
            <div className="space-y-2">
              {versioner.map(v => {
                const id = String(v._id);
                const vald = valda.includes(id);
                const sparrad = !vald && valda.length >= MAX_ANTAL;
                return (
                  <label
                    key={id}
                    className={`flex items-center gap-3 p-3 border rounded-lg ${
                      vald ? 'border-leadme-pink bg-pink-50' : 'border-gray-200'
                    } ${sparrad ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}
                  >
                    <input
                      type="checkbox"
                      checked={vald}
                      disabled={sparrad}
                      onChange={() => vaxla(id)}
                      className="w-4 h-4 text-leadme-pink border-gray-300 rounded focus:ring-leadme-pink"
                    />
                    <span className="text-sm text-gray-900 font-medium">
                      {versionsText(v)}{v.senaste ? ' (senaste)' : ''}
                    </span>
                    {v.sentAt && <span className="text-xs text-gray-500 ml-auto">{datumText(v.sentAt)}</span>}
                  </label>
                );
              })}
            </div>
            {valda.length >= MAX_ANTAL && (
              <p className="text-xs text-amber-700 mt-3">Du kan jämföra högst {MAX_ANTAL} versioner åt gången.</p>
            )}
          </div>
          <div className="px-5 py-4 bg-gray-50 border-t flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100">
              Avbryt
            </button>
            <button
              onClick={() => setVisar(true)}
              disabled={valda.length < 2}
              className="px-4 py-2 bg-leadme-pink text-white rounded-lg hover:bg-leadme-pink-dark disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Jämför versioner
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-white z-50 overflow-hidden">
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white shadow-sm gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setVisar(false)}
              className="text-sm text-gray-500 hover:text-gray-700 flex-shrink-0"
            >
              ← Välj andra
            </button>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">Jämför versioner</h2>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0">
            <label className="hidden md:flex items-center gap-2 cursor-pointer select-none">
              <span className="text-sm text-gray-600">Synkad scrollning</span>
              <button
                type="button"
                role="switch"
                aria-checked={synkadScroll}
                onClick={() => setSynkadScroll(s => !s)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-leadme-pink focus:ring-offset-2 ${
                  synkadScroll ? 'bg-leadme-pink' : 'bg-gray-200'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  synkadScroll ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </label>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2" aria-label="Stäng">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto md:overflow-hidden">
          <div className={`md:h-full grid gap-4 p-4 grid-cols-1 ${
            valdaVersioner.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3'
          }`}>
            {valdaVersioner.map((v, index) => (
              <div key={v._id} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm flex flex-col md:min-h-0">
                <div className={`p-4 border-b border-gray-200 ${v.senaste ? 'bg-green-50' : 'bg-gray-50'}`}>
                  <h3 className="font-semibold text-gray-900">
                    {versionsText(v)}{v.senaste ? ' · senaste' : ''}
                  </h3>
                  {v.sentAt && <p className="text-sm text-gray-500">Skickad {datumText(v.sentAt)}</p>}
                </div>
                <div
                  ref={(el) => { scrollRefs.current[index] = el; }}
                  className="md:flex-1 md:overflow-y-auto p-2 sm:p-4"
                  onScroll={(e) => handleScroll(index, e.target.scrollTop)}
                >
                  {renderVersion(v._id)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VersionsJamforelse;
